import { Button, Badge, cn } from '@design-system';
import { Copy, ExternalLink, FlaskConical, Shield, Send, Heart, Loader2 } from 'lucide-react';
import { AdvisorWarningDialog } from '@/components/AdvisorWarningDialog';
import { IncompleteIntakeDialog } from '@/shared/components/incomplete-intake-dialog';
import { OnboardingOverview } from '@/pages/OnboardingOverview';
import { PageLink } from '@/components/PageLink';

import { useNavigateToPage } from '@client';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  canAccessSendReminder,
  shouldShowSendReminder,
  useSendReminderStore,
} from '@/features/send-reminder';
import { PilotRestricted } from '@/shared/components/pilot-restricted';
import { useIsFreemiumAdvisor } from '@/shared/hooks/use-freemium-advisor';
import { useIsPerClientInviteLinkEnabled } from '@/shared/hooks/use-per-client-invite-link';
import { useCopyClientInviteLink } from '@/shared/hooks/use-copy-client-invite-link';
import { useActivePilotProgram } from '@/shared/hooks/use-active-pilot-program';

import {
  findClientPartner,
  fullName as sharedFullName,
  NYL_PILOT_ADVISOR_FEATURE_FLAG,
} from '@shared';
import { SurveySubmissionSource } from '@shared/surveySubmissionSource';
import { clientNeedsFullIntakeForm } from '@/shared/lib/utils';
import { useValidatedIntakeNavigation } from '@/shared/hooks/use-validated-intake-navigation';
import { withReturnToClients } from '@/shared/hooks/use-back-destination';
import { useIsGenworthMode } from '@/features/genworth/hooks/use-genworth-mode';
import { INTAKE_FORM_INCOMPLETE, ERROR_OR_INCOMPLETE_STATUS_TAGS } from '@client';

function canViewResults(clientTags: string[]) {
  return !clientTags.some(tag => ERROR_OR_INCOMPLETE_STATUS_TAGS.includes(tag));
}

export function isPrefillableInviteClient(
  client: Pick<Client, 'hasPrefillableInviteLink'>,
  isPerClientInviteLinkEnabled: boolean,
): boolean {
  return isPerClientInviteLinkEnabled && client.hasPrefillableInviteLink === true;
}

export function canOpenAgentEnteredIntakeForm(
  client: Pick<Client, 'agentEnteredIntakeStatus' | 'formReferenceId'>,
  needsFullIntake: boolean,
): boolean {
  return (
    client.agentEnteredIntakeStatus === 'consented' && !!client.formReferenceId && needsFullIntake
  );
}

export function shouldHideExploreQuotesForPilot(pilotFlagKey: string | null | undefined): boolean {
  return pilotFlagKey === NYL_PILOT_ADVISOR_FEATURE_FLAG;
}

interface ClientNameCellProps {
  clients: Client[];
  client: Client;
  sessionAdvisor: Advisor;
  showExploreQuotes?: boolean;
}

export function ClientNameCell({
  clients,
  client,
  sessionAdvisor,
  showExploreQuotes = true,
}: ClientNameCellProps) {
  const navigateToPage = useNavigateToPage();
  const navigate = useNavigate();
  const isPerClientInviteLink = useIsPerClientInviteLinkEnabled();
  const { copyInviteLink, copyingClientId } = useCopyClientInviteLink();
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);
  const { openWithClient } = useSendReminderStore();
  const isGenworthMode = useIsGenworthMode();
  const hasResults = canViewResults(client.clientTags);
  const needsFullIntake = clientNeedsFullIntakeForm(client, isGenworthMode);
  const isDifferentAdvisor = sessionAdvisor.advisorId !== client.advisorId;
  const { validateAndDecide, isValidating } = useValidatedIntakeNavigation(client);
  const isFreemiumAdvisor = useIsFreemiumAdvisor();
  const activePilotProgram = useActivePilotProgram();
  const canOpenReminder = canAccessSendReminder(client);
  const showSendReminder = shouldShowSendReminder(client);
  const hasPrefillableInviteLink = isPrefillableInviteClient(client, isPerClientInviteLink);
  const canOpenAgentEnteredIntake = canOpenAgentEnteredIntakeForm(client, needsFullIntake);

  const handleNavigateToClient = useCallback(async () => {
    const { needsFullIntake: freshNeedsFullIntake } = await validateAndDecide();
    if (freshNeedsFullIntake) {
      setIncompleteDialogOpen(true);
    } else {
      navigateToPage(OnboardingOverview, { clientId: client.clientId });
    }
  }, [navigateToPage, client.clientId, validateAndDecide]);

  const handleExploreQuotes = useCallback(() => {
    navigate(withReturnToClients(`/clients/${client.clientId}/onboarding/shop-insurance`));
  }, [navigate, client.clientId]);

  const handleSendReminder = useCallback(() => {
    openWithClient(client);
  }, [openWithClient, client]);

  const handleOpenAgentEnteredIntake = useCallback(() => {
    if (!client.formReferenceId) return;
    window.open(
      `${window.location.origin}/onboarding-form/${client.formReferenceId}?source=${SurveySubmissionSource.FILL_FORM}`,
      '_blank',
      'noopener,noreferrer',
    );
  }, [client.formReferenceId]);

  const partner = findClientPartner(client, clients);

  const nameButton = (
    <Button
      size="sm"
      variant="ghost"
      disabled={!hasResults || isValidating}
      className={cn('text-primary-dark hover:bg-primary/5 h-8 px-2 font-medium', {
        'pr-1': client.isSampleClient,
      })}
    >
      {sharedFullName(client)}
      {isValidating && <Loader2 className="size-3 animate-spin" />}
      {client.isSampleClient && (
        <Badge variant="warning">
          <FlaskConical className="size-3" />
          Sample Client
        </Badge>
      )}
    </Button>
  );

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-1">
        {!hasResults ? (
          nameButton
        ) : isDifferentAdvisor ? (
          <AdvisorWarningDialog
            title="Viewing another advisor's client"
            message="Any changes you make will affect the actual client record and be visible to both the client and their primary advisor."
            onContinue={handleNavigateToClient}
          >
            {nameButton}
          </AdvisorWarningDialog>
        ) : needsFullIntake ? (
          <span
            onClick={handleNavigateToClient}
            className={cn('cursor-pointer', isValidating && 'cursor-wait')}
          >
            {nameButton}
          </span>
        ) : (
          <PageLink
            to={OnboardingOverview}
            targetProps={{ clientId: client.clientId }}
          >
            {nameButton}
          </PageLink>
        )}
      </div>
      {partner && (
        <span className="text-muted-foreground flex items-center gap-1 pl-1 text-xs">
          <Heart className="size-3 fill-rose-400 text-rose-400" />
          Coupled with {sharedFullName(partner)}
        </span>
      )}
      {showExploreQuotes && hasResults && client.isQuoteAvailable && (!isFreemiumAdvisor || client.isSampleClient) && (
        <PilotRestricted
          capability="explore-quotes"
          hidden={shouldHideExploreQuotesForPilot(activePilotProgram?.flagKey)}
        >
          <Button
            size="sm"
            variant="outline"
            onClick={handleExploreQuotes}
            title={`Explore quotes for ${sharedFullName(client)}`}
            className="pl-1.5! h-7 w-fit self-start pr-2 text-xs md:opacity-0 md:group-hover:opacity-100"
          >
            <Shield className="size-3.5" />
            Explore Quotes
          </Button>
        </PilotRestricted>
      )}
      {/* A pre-filled invite drops the incomplete-intake tag once its plan is built, so the
          link stays copyable for as long as the client still owes us the full form. */}
      {hasPrefillableInviteLink ? (
        needsFullIntake && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => copyInviteLink(client.clientId)}
            disabled={copyingClientId === client.clientId}
            title={`Copy invite link for ${sharedFullName(client)}`}
            className="pl-1.5! h-7 w-fit self-start pr-2 text-xs md:opacity-0 md:group-hover:opacity-100"
          >
            <Copy className="size-3.5" />
            {copyingClientId === client.clientId ? 'Copying...' : 'Copy Invite Link'}
          </Button>
        )
      ) : canOpenAgentEnteredIntake ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={handleOpenAgentEnteredIntake}
          title={`Open intake form for ${sharedFullName(client)}`}
          className="pl-1.5! h-7 w-fit self-start pr-2 text-xs md:opacity-0 md:group-hover:opacity-100"
        >
          <ExternalLink className="size-3.5" />
          Open Intake Form
        </Button>
      ) : (
        client.clientTags.includes(INTAKE_FORM_INCOMPLETE) && showSendReminder && (
          <PilotRestricted capability="client-reminder">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSendReminder}
              disabled={!canOpenReminder}
              title={`Send reminder to ${sharedFullName(client)}`}
              className="pl-1.5! h-7 w-fit self-start pr-2 text-xs md:opacity-0 md:group-hover:opacity-100 md:group-hover:disabled:opacity-50"
            >
              <Send className="size-3.5" />
              Send Reminder
            </Button>
          </PilotRestricted>
        )
      )}
      <IncompleteIntakeDialog
        client={client}
        open={incompleteDialogOpen}
        onOpenChange={setIncompleteDialogOpen}
      />
    </div>
  );
}
